import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreditTopUpDetailDialog } from '@/components/messaging/credit-top-up-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { StatCard } from '@/components/shared/stat-card';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime, formatMoney, formatNumber } from '@/utils/format';

interface CreditsSummary {
  balanceMinor: number;
  currency: string;
  lowBalanceThresholdMinor: number;
  costPerSegmentMinor: number;
  segmentLength: number;
  packsMinor: number[];
  purchaseMinMinor: number;
  purchaseMaxMinor: number;
  paymentsConfigured: boolean;
  testCheckoutAllowed: boolean;
}

interface SmsConnection {
  mode: 'byok' | 'trinity';
  status: 'draft' | 'active' | 'suspended';
  isConfigured: boolean;
}

interface LedgerRow {
  id: string;
  entryType: 'purchase' | 'consumption' | 'adjustment';
  amountMinor: number;
  currency: string;
  balanceAfterMinor: number;
  campaignId: string | null;
  description: string | null;
  createdAt: string;
}

interface CheckoutSessionRow {
  id: string;
  status: 'pending' | 'processing' | 'paid' | 'failed' | 'cancelled' | 'expired';
  creditAmountMinor: number;
  amountMinor: number;
  currency: string;
  failureCode: string | null;
  paidAt: string | null;
  createdAt: string;
}

const checkoutStatusVariant = (status: CheckoutSessionRow['status']): BadgeVariant => {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled' || status === 'expired') return 'neutral';
  return 'info';
};

export const MessageCreditsPanel = () => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { t: tBilling } = useTranslation('billing');
  const queryClient = useQueryClient();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const membership = useCurrentMembership();
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const canManage = hasPermission(membership, PERMISSIONS.MESSAGING_MANAGE_CREDITS);
  const smsMessagingEnabled = allowsFeature('sms_messaging');

  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(25);
  const [checkoutPage, setCheckoutPage] = useState(1);
  const [checkoutPageSize, setCheckoutPageSize] = useState(25);
  const [activePanelTab, setActivePanelTab] = useState<'topUps' | 'activity'>('topUps');
  const [recheckingSessionId, setRecheckingSessionId] = useState<string | null>(null);
  const [detailSessionId, setDetailSessionId] = useState<string | null>(null);
  const [detailSessionStatus, setDetailSessionStatus] = useState<CheckoutSessionRow['status'] | null>(
    null,
  );

  const connection = useQuery({
    queryKey: ['messaging', 'sms-connection'],
    queryFn: () => api.get<SmsConnection>('/messaging/sms-connection'),
  });

  const credits = useQuery({
    queryKey: ['messaging', 'credits'],
    queryFn: () => api.get<CreditsSummary>('/messaging/credits'),
    enabled: canManage,
  });

  const checkoutSessions = useQuery({
    queryKey: ['messaging', 'credits', 'checkout-sessions', checkoutPage, checkoutPageSize],
    queryFn: () =>
      api.get<{
        data: CheckoutSessionRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(
        `/messaging/credits/checkout-sessions?page=${checkoutPage}&pageSize=${checkoutPageSize}`,
      ),
    enabled: canManage,
  });

  const ledger = useQuery({
    queryKey: ['messaging', 'credits', 'ledger', ledgerPage, ledgerPageSize],
    queryFn: () =>
      api.get<{
        data: LedgerRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/messaging/credits/ledger?page=${ledgerPage}&pageSize=${ledgerPageSize}`),
    enabled: canManage,
  });

  const trinityActive =
    connection.data?.isConfigured && connection.data.mode === 'trinity';

  const lowBalance = useMemo(() => {
    if (!credits.data) return false;
    return credits.data.balanceMinor <= credits.data.lowBalanceThresholdMinor;
  }, [credits.data]);

  const recheckCheckout = useMutation({
    mutationFn: (sessionId: string) =>
      api.post<{ id: string; status: string }>(
        `/messaging/credits/checkout-sessions/${sessionId}/recheck`,
        {},
      ),
    onMutate: (sessionId) => {
      setRecheckingSessionId(sessionId);
    },
    onSuccess: async (session) => {
      await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits'] });
      await queryClient.invalidateQueries({ queryKey: ['messaging', 'credits', 'ledger'] });
      await queryClient.invalidateQueries({
        queryKey: ['messaging', 'credits', 'checkout-sessions'],
      });

      if (session.status === 'paid') {
        toast.success(t('credits.purchaseSuccess'));
      } else if (session.status === 'processing' || session.status === 'pending') {
        toast.info(t('credits.purchasePending'));
      } else if (session.status === 'cancelled') {
        toast.info(t('credits.purchaseCancelled'));
      } else if (session.status === 'expired') {
        toast.info(t('credits.purchaseExpired'));
      } else if (session.status === 'failed') {
        toast.info(t('credits.recheckFailed'));
      } else {
        toast.info(t('credits.recheckStillFailed'));
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
    onSettled: () => {
      setRecheckingSessionId(null);
    },
  });

  if (!canManage) {
    return <p className="text-body-sm text-fg-secondary">{t('credits.noPermission')}</p>;
  }

  if (!smsMessagingEnabled) {
    return (
      <PlanUpgradePrompt
        variant="full"
        title={t('credits.upgradeTitle')}
        description={t('credits.upgradeDescription')}
        planNote={t('credits.upgradePlanNote')}
        action={{
          label: tBilling('checkout.title'),
          onClick: () => {
            window.location.assign('/subscriptions');
          },
        }}
      />
    );
  }

  if (connection.isLoading || credits.isLoading) {
    return <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>;
  }

  const summary = credits.data!;
  const segmentsAffordable = Math.floor(summary.balanceMinor / summary.costPerSegmentMinor);

  const checkoutColumns: DataTableColumn<CheckoutSessionRow>[] = [
    {
      id: 'date',
      header: t('credits.topUpColumns.date'),
      cell: (row) => formatDateTime(row.createdAt, { locale, timezone }),
    },
    {
      id: 'amount',
      header: t('credits.topUpColumns.amount'),
      align: 'right',
      cell: (row) => formatMoney(row.amountMinor, row.currency),
    },
    {
      id: 'status',
      header: t('credits.topUpColumns.status'),
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={checkoutStatusVariant(row.status)}>
            {t(`credits.checkoutStatus.${row.status}`)}
          </Badge>
          {row.status !== 'paid' && row.status !== 'cancelled' && row.status !== 'expired' ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={recheckCheckout.isPending && recheckingSessionId === row.id}
              onClick={(event) => {
                event.stopPropagation();
                recheckCheckout.mutate(row.id);
              }}
            >
              {recheckCheckout.isPending && recheckingSessionId === row.id
                ? t('credits.recheckChecking')
                : t('credits.recheckAction')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const ledgerColumns: DataTableColumn<LedgerRow>[] = [
    {
      id: 'date',
      header: t('credits.columns.date'),
      cell: (row) => formatDateTime(row.createdAt, { locale, timezone }),
    },
    {
      id: 'type',
      header: t('credits.columns.type'),
      cell: (row) => (
        <Badge
          variant={
            row.entryType === 'purchase'
              ? 'success'
              : row.entryType === 'consumption'
                ? 'neutral'
                : 'info'
          }
        >
          {t(`credits.entryType.${row.entryType}`)}
        </Badge>
      ),
    },
    {
      id: 'amount',
      header: t('credits.columns.amount'),
      align: 'right',
      cell: (row) => {
        const formatted = formatMoney(Math.abs(row.amountMinor), row.currency);
        if (row.amountMinor > 0) return `+${formatted}`;
        if (row.amountMinor < 0) return `−${formatted}`;
        return formatted;
      },
    },
    {
      id: 'balance',
      header: t('credits.columns.balance'),
      align: 'right',
      cell: (row) => formatMoney(row.balanceAfterMinor, row.currency),
      hideBelowMd: true,
    },
    {
      id: 'detail',
      header: t('credits.columns.detail'),
      cell: (row) => row.description ?? (row.campaignId ? t('credits.messageSend') : '—'),
      hideBelowMd: true,
    },
  ];

  const checkoutPageMeta = checkoutSessions.data?.page;
  const ledgerPageMeta = ledger.data?.page;

  const openTopUpDetail = (row: CheckoutSessionRow) => {
    setActivePanelTab('topUps');
    setDetailSessionId(row.id);
    setDetailSessionStatus(row.status);
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={t('credits.balanceLabel')}
          value={formatMoney(summary.balanceMinor, summary.currency)}
          loading={credits.isLoading}
        />
        <StatCard
          label={t('credits.rateLabel')}
          value={formatMoney(summary.costPerSegmentMinor, summary.currency)}
          hint={t('credits.rateHelp', { length: summary.segmentLength })}
        />
        <StatCard
          label={t('credits.segmentsLabel')}
          value={formatNumber(segmentsAffordable)}
          hint={t('credits.segmentsHelp')}
        />
      </div>

      {lowBalance && trinityActive ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm text-fg">
          {t('credits.lowBalance', {
            amount: formatMoney(summary.balanceMinor, summary.currency),
          })}
        </p>
      ) : null}

      <Tabs
        value={activePanelTab}
        onValueChange={(value) => setActivePanelTab(value as 'topUps' | 'activity')}
      >
        <TabsList>
          <TabsTrigger value="topUps">{t('credits.panelTabs.topUps')}</TabsTrigger>
          <TabsTrigger value="activity">{t('credits.panelTabs.activity')}</TabsTrigger>
        </TabsList>

        <TabsContent value="topUps" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">{t('credits.topUpHistoryDescription')}</p>
          <DataTable
            rows={checkoutSessions.data?.data ?? []}
            columns={checkoutColumns}
            rowKey={(row) => row.id}
            loading={checkoutSessions.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('credits.topUpEmpty')}</p>}
            onRowClick={openTopUpDetail}
          />
          {checkoutPageMeta && checkoutPageMeta.totalPages > 1 ? (
            <Pagination
              page={checkoutPageMeta.page}
              pageSize={checkoutPageMeta.pageSize}
              totalItems={checkoutPageMeta.totalItems}
              totalPages={checkoutPageMeta.totalPages}
              onPageChange={setCheckoutPage}
              onPageSizeChange={(next) => {
                setCheckoutPageSize(next);
                setCheckoutPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="activity" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">{t('credits.historyDescription')}</p>
          <DataTable
            rows={ledger.data?.data ?? []}
            columns={ledgerColumns}
            rowKey={(row) => row.id}
            loading={ledger.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('credits.empty')}</p>}
          />
          {ledgerPageMeta && ledgerPageMeta.totalPages > 1 ? (
            <Pagination
              page={ledgerPageMeta.page}
              pageSize={ledgerPageMeta.pageSize}
              totalItems={ledgerPageMeta.totalItems}
              totalPages={ledgerPageMeta.totalPages}
              onPageChange={setLedgerPage}
              onPageSizeChange={(next) => {
                setLedgerPageSize(next);
                setLedgerPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      <CreditTopUpDetailDialog
        key={detailSessionId ?? 'closed'}
        sessionId={detailSessionId}
        initialStatus={detailSessionStatus}
        open={detailSessionId != null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailSessionId(null);
            setDetailSessionStatus(null);
          }
        }}
      />
    </div>
  );
};
